import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getRole, RoleKey } from '../config/roles';
import { McCard, McButton } from '@maersk-global/mds-react-wrapper/components-core';
import styles from './HomePage.module.css';

interface FeatureCard {
    title: string;
    description: string;
    category: string;
    path: string;
    actionLabel: string;
    image: string;
    disabled?: boolean;
    tag?: string;
    roleKey?: RoleKey;
}

const FEATURES: FeatureCard[] = [
    {
        category: 'Data Access',
        title: 'Queries',
        description: 'Create, manage, and execute SQL queries across Oracle and PostgreSQL terminals with built-in validation and version control.',
        path: '/queries',
        actionLabel: 'Open Queries',
        image: '/images/cards/queries.svg',
    },
    {
        category: 'Artificial Intelligence',
        title: 'AI Agents',
        description: 'Build intelligent agents with a visual drag-and-drop builder. Connect tools, configure triggers, and deploy across WhatsApp, Teams, and Web.',
        path: '/agents',
        actionLabel: 'Open Agents',
        image: '/images/cards/ai-agents.svg',
        roleKey: 'viewAgents',
    },
    {
        category: 'Automation',
        title: 'Schedules',
        description: 'Automate recurring data workflows with cron-based scheduling. Monitor execution history and manage delivery pipelines.',
        path: '/schedules',
        actionLabel: 'Open Schedules',
        image: '/images/cards/schedules.svg',
        roleKey: 'viewSchedules',
    },
    {
        category: 'Compliance',
        title: 'Regions',
        description: 'Configure regional data access policies and terminal-specific settings to ensure regulatory compliance.',
        path: '/regions',
        actionLabel: 'Open Regions',
        image: '/images/cards/regions.svg',
        roleKey: 'viewRegions',
    },
    {
        category: 'Integration',
        title: 'Developer Docs',
        description: 'API reference, AI Agent builder guide, tool catalog, and integration documentation for engineering teams.',
        path: '/developer-docs',
        actionLabel: 'Open Docs',
        image: '/images/cards/developer-docs.svg',
        roleKey: 'viewDevDocs',
    },
    {
        category: 'Access',
        title: 'DB Breakglass',
        description: 'Raise an emergency DB breakglass access request for a terminal against an incident for a limited duration.',
        path: '/db-breakglass',
        actionLabel: 'Raise Request',
        image: '/images/cards/db-breakglass.svg',
        roleKey: 'executeDbBreakglass',
    },
    {
        category: 'Events',
        title: 'Events subscription',
        description: 'Subscribe to real-time platform events and data change notifications across terminals.',
        path: '/events',
        actionLabel: 'Coming Soon',
        image: '/images/cards/events.svg',
        disabled: true,
        tag: 'Coming soon',
    },
    {
        category: 'Support',
        title: 'FAQ & Support',
        description: 'Best practices, training materials, and answers to frequently asked questions for platform users.',
        path: '/faq',
        actionLabel: 'Open FAQ',
        image: '/images/cards/support.svg',
        roleKey: 'viewFaq',
    },
];

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const { hasRole } = useAuth();

    return (
        <div className={styles.container}>
            <div className={styles.hero}>
                <h1 className={`mds-headline--x-large-bold ${styles.title}`}>
                    CDH Enterprise Intelligent Platform
                </h1>
                <p className={`mds-text--large-normal ${styles.subtitle}`}>
                    A self-hosted, enterprise-grade platform built by APM Terminals.
                    <br />
                    Empowering global operations with a unified single source of truth,
                    proactive AI insights, and governed data democratization.
                </p>
            </div>

            <div className={styles.grid}>
                {FEATURES.filter(feature => {
                    if (!feature.roleKey) return true;
                    return hasRole(getRole(feature.roleKey));
                }).map((feature) => (
                    <McCard
                        key={feature.title}
                        fit="small"
                        heading={feature.title}
                        className={`${styles.card} ${feature.disabled ? styles.cardDisabled : ''}`}
                        /* @ts-ignore */
                        hasImageSlot
                        hassubheadingslot
                        hasactionsslot
                    >
                        {/* Image slot */}
                        <div slot="image" className={styles.imageSlot}>
                            <img src={feature.image} alt={feature.title} className={styles.cardImage} />
                        </div>

                        {/* Subheading: category + tag + description */}
                        <div slot="subheading">
                            <div className={styles.categoryLabel}>
                                {feature.category}
                                {feature.tag && (
                                    <span className={styles.futureTag}>{feature.tag}</span>
                                )}
                            </div>
                            <div className={`mds-text--medium-normal ${styles.description}`}>
                                {feature.description}
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div slot="actions" className={styles.actions}>
                            <McButton
                                label={feature.actionLabel}
                                appearance="primary"
                                variant="outlined"
                                fit="medium"
                                disabled={feature.disabled}
                                onClick={() => !feature.disabled && navigate(feature.path)}
                            />
                        </div>
                    </McCard>
                ))}
            </div>
        </div>
    );
};

export default HomePage;
